import { useMemo, useState } from 'react';
import { Loader2, Receipt, ChevronDown, ChevronUp } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import type { FacturacionSerenoResult } from '@/lib/types';
import { classNames } from '@/lib/utils';
import InfoTooltip from '@/components/ui/InfoTooltip';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const MODELO_LABEL: Record<string, string> = {
  gestion: 'Gestión',
  explotacion: 'Explotación',
  explotacion_lcm: 'Explotación LCM',
  rent2rent: 'Rent2rent',
  otros: 'Otros servicios',
};

function fmtMoney(value: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTH_SHORT[Number(m) - 1] || m} ${String(y).slice(2)}`;
}

function fechaCorta(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(d)} ${MONTH_SHORT[Number(m) - 1]?.toLowerCase()} ${y}`;
}

/**
 * «Facturación Sereno»: la cifra de Holded, no el volumen de reservas. Va
 * encima de todo en Finance > Resumen con los filtros aplicados al lado, porque
 * el descuadre con Holded venía casi siempre de mirar con «Solo activas» y «Sin
 * Alicante» puestos sin darse cuenta.
 */
export default function FacturacionSereno({
  data,
  loading,
  filtros,
  onVerComoHolded,
}: {
  data: FacturacionSerenoResult | null;
  loading: boolean;
  /** Filtros con los que se ha calculado, en texto, para enseñarlos junto a la cifra. */
  filtros: string[];
  /** Quita los filtros que Holded no tiene y recalcula. */
  onVerComoHolded: () => void;
}) {
  const isDark = useIsDarkMode();
  const [verPropietarios, setVerPropietarios] = useState(false);
  const color = {
    grid: isDark ? '#ffffff14' : '#0B1D2E15',
    axis: isDark ? '#9ca3af' : '#0B1D2E99',
    cursor: isDark ? '#ffffff10' : '#0B1D2E08',
    explotacion: isDark ? '#D1E0F0' : '#0B1D2E',
    gestion: isDark ? '#6FA8DC' : '#3B6EA5',
    estimada: isDark ? '#3B6EA5' : '#A3C2E0',
    previsto: isDark ? '#ffffff47' : '#0B1D2E29',
  };

  const chart = useMemo(
    () =>
      (data?.byMonth || []).map((m) => ({
        name: monthLabel(m.month),
        Explotación: Math.round(m.explotacion),
        Gestión: Math.round(m.gestionLiquidada),
        'Gestión (estimada)': Math.round(m.gestionEstimada),
        Previsto: Math.round(m.previsto),
      })),
    [data]
  );

  if (!data) {
    return (
      <div className="card mb-6 flex items-center gap-3 text-sm text-sereno-dark/50">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
        {loading ? 'Calculando la facturación de Sereno…' : 'No se ha podido calcular la facturación de Sereno.'}
      </div>
    );
  }

  const hayEstimada = data.gestionEstimada > 0;
  const hayPrevisto = data.previsto > 0;
  // Hasta diciembre de 2025 las cifras son las facturas de Holded tal cual
  const holded = data.fuente?.holded;
  const soloHolded = !!holded && !data.fuente?.crm;

  return (
    <div className={classNames('card mb-6', loading && 'opacity-60 transition-opacity')}>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Facturación Sereno</h2>
        <InfoTooltip text="Lo que factura Sereno, sin IVA: lo mismo que suma Holded. Hasta diciembre de 2025 son directamente las facturas de Holded, por fecha de factura. Desde 2026 se calcula en el CRM: en explotación, una factura por estancia (su total sin el 10 %) con fecha del día de salida. En gestión, la factura mensual al propietario —comisión, limpieza, renting, comisión OTA, mantenimiento y tusereno.es— con fecha del último día del mes, sacada de las liquidaciones guardadas. No es lo que pagan los huéspedes: eso es el «Volumen de reservas»." />
        {loading && <Loader2 size={14} className="animate-spin text-sereno-dark/40" />}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="text-3xl font-bold">{fmtMoney(data.total)}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {holded && (
            <span className="rounded-full bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200 px-2.5 py-1 text-xs font-medium">
              {soloHolded ? 'Facturas de Holded' : `Holded hasta ${fechaCorta(holded.to)}`}
            </span>
          )}
          {soloHolded ? null : filtros.length === 0 ? (
            <span className="rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2.5 py-1 text-xs font-medium">
              Todos los pisos, como Holded
            </span>
          ) : (
            filtros.map((f) => (
              <span key={f} className="rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 px-2.5 py-1 text-xs font-medium">
                {f}
              </span>
            ))
          )}
        </div>
      </div>
      {holded && (
        <p className="mt-2 text-xs text-sereno-dark/50">
          {soloHolded
            ? 'Antes de 2026 la cifra sale directamente de las facturas de Holded (base sin IVA, por fecha de factura). Los filtros de piso, zona y canal no se aplican a esos años.'
            : `Hasta el ${fechaCorta(holded.to)} la cifra sale de las facturas de Holded, sin filtros; desde 2026, del cálculo del CRM.`}
        </p>
      )}
      {filtros.length > 0 && !soloHolded && (
        <p className="mt-2 text-xs text-sereno-dark/50">
          Holded no tiene estos filtros: para comparar con Holded,{' '}
          <button onClick={onVerComoHolded} className="font-semibold text-sereno-dark underline underline-offset-2 min-h-11 sm:min-h-0">
            quítalos
          </button>
          .
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-sereno-dark/50 mb-1">Explotación · facturas a huéspedes</p>
          <p className="text-lg font-bold">{fmtMoney(data.explotacion)}</p>
          <p className="text-xs text-sereno-dark/40">
            {soloHolded ? `${data.contadores.estancias} facturas a huéspedes` : `${data.contadores.estancias} estancias`}
          </p>
        </div>
        <div>
          <p className="text-xs text-sereno-dark/50 mb-1">Gestión · facturas a propietarios</p>
          <p className="text-lg font-bold">{fmtMoney(data.gestion)}</p>
          <p className="text-xs text-sereno-dark/40 flex items-center gap-1 flex-wrap">
            <span>
              {soloHolded
                ? `${holded!.facturasPropietario} facturas F${holded!.otros ? ` · incluye ${fmtMoney(holded!.otros)} de otros servicios` : ''}`
                : `${data.contadores.liquidaciones} ${holded ? 'facturas y liquidaciones' : 'liquidaciones'}`}
            </span>
            {hayEstimada && (
              <>
                <span>
                  · {fmtMoney(data.gestionEstimada)} estimados ({data.contadores.mesesEstimados}{' '}
                  {data.contadores.mesesEstimados === 1 ? 'mes' : 'meses'} sin liquidar)
                </span>
                <InfoTooltip
                  text={`Meses ya cerrados sin liquidación guardada en el CRM: se estiman pasando sus reservas por el motor de liquidaciones, sin gastos de mantenimiento, así que suelen quedarse algo por debajo de la factura real. ${data.estimados
                    .map((e) => `${e.owner} (${monthLabel(e.month)}): ${fmtMoney(e.importe)}`)
                    .join(' · ')}`}
                />
              </>
            )}
          </p>
        </div>
        {hayPrevisto && (
          <div>
            <p className="text-xs text-sereno-dark/50 mb-1">Pendiente de facturar</p>
            <p className="text-lg font-bold text-sereno-dark/50">{fmtMoney(data.previsto)}</p>
            <p className="text-xs text-sereno-dark/40">
              Salidas después del {fechaCorta(data.hoy)} y meses sin cerrar. No entra en el total.
            </p>
          </div>
        )}
      </div>

      {chart.length > 0 && (
        <div className="mt-5">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={color.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: color.axis }} />
              <YAxis tick={{ fontSize: 12, fill: color.axis }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: any) => fmtMoney(Number(v))} cursor={{ fill: color.cursor }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Explotación" stackId="f" fill={color.explotacion} />
              <Bar dataKey="Gestión" stackId="f" fill={color.gestion} />
              {hayEstimada && <Bar dataKey="Gestión (estimada)" stackId="f" fill={color.estimada} />}
              {hayPrevisto && <Bar dataKey="Previsto" stackId="f" fill={color.previsto} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.byOwner.length > 0 && (
        <div className="mt-4 border-t border-sereno-dark/10 pt-3">
          <button
            onClick={() => setVerPropietarios((v) => !v)}
            className="min-h-11 flex items-center gap-1.5 text-sm font-medium text-sereno-dark/70 hover:text-sereno-dark"
          >
            {verPropietarios ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Por propietario ({data.byOwner.length})
          </button>
          {verPropietarios && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
                    <th className="py-2 font-medium">Propietario</th>
                    <th className="py-2 font-medium">Modelo</th>
                    <th className="py-2 font-medium text-right">Explotación</th>
                    <th className="py-2 font-medium text-right">Gestión</th>
                    <th className="py-2 font-medium text-right">Total</th>
                    {hayPrevisto && <th className="py-2 font-medium text-right">Pendiente</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.byOwner.map((o) => {
                    const gestion = o.gestionLiquidada + o.gestionEstimada;
                    return (
                      <tr key={o.ownerId || o.owner} className="border-b border-sereno-dark/5 last:border-0">
                        <td className="py-2">
                          {o.owner}
                          {o.origen === 'holded' && !soloHolded && (
                            <span className="ml-1.5 rounded bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200 px-1.5 py-0.5 text-[10px] font-medium">Holded</span>
                          )}
                        </td>
                        <td className="py-2 text-sereno-dark/60">{(o.modelo && MODELO_LABEL[o.modelo]) || o.modelo || '—'}</td>
                        <td className="py-2 text-right">{o.explotacion ? fmtMoney(o.explotacion) : '—'}</td>
                        <td className="py-2 text-right" title={o.gestionEstimada ? `${fmtMoney(o.gestionEstimada)} estimados` : undefined}>
                          {gestion ? `${o.gestionEstimada ? '≈ ' : ''}${fmtMoney(gestion)}` : '—'}
                        </td>
                        <td className="py-2 text-right font-semibold">{fmtMoney(o.total)}</td>
                        {hayPrevisto && <td className="py-2 text-right text-sereno-dark/50">{o.previsto ? fmtMoney(o.previsto) : '—'}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hayEstimada && <p className="mt-2 text-xs text-sereno-dark/40">≈ incluye meses estimados por no tener liquidación guardada.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
